import { useState } from "react";
import type { AssembledContract, Block, ContractPart, CoverSpec } from "../../contracts/types";

function Cover({ spec, master }: { spec: CoverSpec; master?: boolean }) {
  return (
    <div className={`cover ${master ? "cover-master" : ""}`}>
      {spec.kicker && <div className="cover-kicker">{spec.kicker}</div>}
      <div className="cover-title">{spec.title}</div>
      <div className="cover-rule" />
      {spec.subtitle && <div className="cover-subtitle">{spec.subtitle}</div>}
      {(spec.contractLabel || spec.standardLine || spec.forParty || (spec.preparedBy && spec.preparedBy.length > 0)) && (
        <div className="cover-print">
          {spec.contractLabel && <div className="cp-label">{spec.contractLabel}</div>}
          {spec.standardLine && <div className="cp-standard">{spec.standardLine}</div>}
          {spec.date && <div className="cp-muted">{spec.date}</div>}
          {spec.forParty && (
            <div className="cp-block">
              <div className="cp-tag">FOR</div>
              <div className="cp-strong">{spec.forParty}</div>
            </div>
          )}
          {spec.preparedBy && spec.preparedBy.length > 0 && (
            <div className="cp-block">
              <div className="cp-tag">PREPARED BY</div>
              {spec.preparedBy.map((l, i) => <div key={i} className={i === 0 ? "cp-strong" : "cp-muted"}>{l}</div>)}
            </div>
          )}
          {spec.projectNo && <div className="cp-tag" style={{ marginTop: 18 }}>{spec.projectNo}</div>}
        </div>
      )}
      {spec.meta && spec.meta.length > 0 && (
        <table className="cover-meta">
          <tbody>
            {spec.meta.map((m, i) => (
              <tr key={i}>
                <td className="cm-label">{m.label}</td>
                <td className="cm-value">{m.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BlockView({ b }: { b: Block }) {
  switch (b.type) {
    case "heading": {
      const H = `h${Math.min(b.level + 1, 4)}` as "h2" | "h3" | "h4";
      return <H className={`blk-h blk-h${b.level}`}>{b.text}</H>;
    }
    case "paragraph":
      return <p style={b.align === "left" ? { textAlign: "left" } : undefined}>{b.text}</p>;
    case "clause":
      return <p className={`clause ${b.text.startsWith("     ") ? "clause-indent" : ""}`}>{b.text.trim()}</p>;
    case "list":
      return (
        <div className="doc-list">
          {b.items.map((it, i) => (
            <div className="li" key={i} style={{ marginLeft: (it.level || 0) * 22 }}>
              {it.prefix && <span className="li-prefix">{it.prefix}</span>}
              <span className="li-text">{it.text}</span>
            </div>
          ))}
        </div>
      );
    case "table":
      return (
        <table className="doc-table">
          <thead>
            <tr>{b.columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {b.rows.map((r, i) => (
              <tr key={i}>{r.map((v, j) => <td key={j}>{v}</td>)}</tr>
            ))}
          </tbody>
        </table>
      );
    case "signblock":
      return (
        <div className="signblock">
          <div className="sb-title">Execution</div>
          <div className="sb-grid">
            {b.parties.map((p, i) => (
              <div className="sb-col" key={i}>
                {p.caption && <div className="sb-caption">{p.caption}</div>}
                <div className="sb-role">Signed for and on behalf of the {p.role}</div>
                <div className="sb-line"><span>Name:</span> {p.name || ""}</div>
                <div className="sb-line"><span>Title:</span> {p.title || ""}</div>
                {p.signatureDataUri
                  ? <div className="sb-line sig"><span>Signature:</span> <img className="sb-sig-img" src={p.signatureDataUri} alt="signature" /></div>
                  : <div className="sb-line sig"><span>Signature:</span></div>}
                <div className="sb-line sig"><span>Date:</span> {p.signedDate || ""}</div>
                {(p.witnessName !== undefined || p.witnessAddress !== undefined) && (
                  <>
                    <div className="sb-witness">In the presence of (Witness)</div>
                    <div className="sb-line"><span>Name:</span> {p.witnessName || ""}</div>
                    <div className="sb-line"><span>Address:</span> {p.witnessAddress || ""}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    case "spacer":
      return <div style={{ height: 16 }} />;
    case "pagebreak":
      return <div className="pagebreak" />;
  }
}

function PartView({ part, bare }: { part: ContractPart; bare?: boolean }) {
  return (
    <div>
      {!bare && <Cover spec={part.cover} />}
      <div className="part-body">
        {part.blocks.map((b, i) => <BlockView key={i} b={b} />)}
      </div>
    </div>
  );
}

export function ContractPreview({ contract }: { contract: AssembledContract }) {
  // A letter prints as the letter itself — no title page, no contents, no part
  // cover (its letterhead is the top of the page). Mirrors the same noCover gate
  // in buildDocument/printContract, so a shared letter matches the export.
  const bare = !!contract.noCover;
  const tabs = bare ? contract.parts.map((p) => p.name) : ["Cover", "Contents", ...contract.parts.map((p) => p.name)];
  const [active, setActive] = useState(0);

  return (
    <div className="preview-inner">
      {tabs.length > 1 && (
        <div className="preview-tabs">
          {tabs.map((t, i) => (
            <button key={i} className={`ptab ${i === active ? "active" : ""}`} onClick={() => setActive(i)}>
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="paper-wrap">
        <div className="paper">
          {bare ? (
            <PartView part={contract.parts[active]} bare />
          ) : active === 0 ? (
            <>
              <Cover spec={contract.masterCover} master />
              <div className="paper-notice">{contract.notice}</div>
            </>
          ) : active === 1 ? (
            <div className="contents">
              <h2 className="contents-h">Contents</h2>
              <div className="contents-rule" />
              {contract.parts.map((p, i) => (
                <div className="toc-row" key={i}>
                  <span className="toc-num">{i + 1}.</span>
                  <span className="toc-name">{p.name}</span>
                  <span className="toc-dots" />
                </div>
              ))}
            </div>
          ) : (
            <PartView part={contract.parts[active - 2]} />
          )}
        </div>
      </div>
    </div>
  );
}
